import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import { SUBSCRIPTION_KEY } from './hooks.js';
import type { CurrentSubscriptionRow } from './access.js';

/**
 * Native subscription management — the client side of the verify-after-write
 * Pages Functions (paddle-details / paddle-cancel / paddle-resume /
 * paddle-card-txn).
 *
 * Trust contract, mirrored from the server (see DECISIONS.md 2026-05-04 and
 * its 2026-07-16 supersession): the UI state after any mutation comes ONLY
 * from the canonical row Paddle confirmed, which every endpoint returns.
 * We write that row into the React Query cache; nothing here flips state
 * optimistically.
 */

/** Mutable subscription columns as returned by the Pages Functions. */
export interface CanonicalRow {
  /** Who is billing this subscription. Drives the whole management UI: on
   *  'google_play' every action lives in Google's app, not ours; on 'gift'
   *  there is nothing to manage at all. */
  provider?: string;
  status: string;
  trial_ends_at: string | null;
  current_period_starts_at: string | null;
  current_period_ends_at: string | null;
  cancelled_at: string | null;
  provider_price_id: string | null;
}

export interface PaymentMethodSummary {
  brand: string;
  last4: string;
  expiry_month: number | null;
  expiry_year: number | null;
}

export interface TransactionSummary {
  id: string;
  billed_at: string | null;
  status: string;
  /** Lowest denomination, e.g. "500" = 5.00 USD. */
  total: string;
  currency: string;
}

export interface SubscriptionDetails {
  /** null = free tier (never subscribed / no row). */
  row: CanonicalRow | null;
  payment_method: PaymentMethodSummary | null;
  transactions: TransactionSummary[] | null;
}

export class ManageError extends Error {
  constructor(
    public code: string,
    public status: number,
  ) {
    super(code);
    this.name = 'ManageError';
  }
}

async function authedPost<T>(path: string): Promise<T> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new ManageError('not_signed_in', 401);
  const r = await fetch(path, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  let body: unknown = null;
  try {
    body = await r.json();
  } catch {
    body = null;
  }
  if (!r.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'request_failed';
    throw new ManageError(code, r.status);
  }
  return body as T;
}

export const SUBSCRIPTION_DETAILS_KEY = (userId: string) =>
  ['subscription', 'details', userId] as const;

/**
 * The subscription screen's single data source — live from Paddle.
 * A 404 (free tier) resolves to { row: null, ... }, not an error.
 */
export function useSubscriptionDetails(): UseQueryResult<SubscriptionDetails> {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? SUBSCRIPTION_DETAILS_KEY(user.id) : ['subscription', 'details', 'anon'],
    enabled: !!user,
    staleTime: 15 * 1000,
    retry: 1,
    queryFn: async (): Promise<SubscriptionDetails> => {
      /**
       * Google Play subscribers are answered from the local row and never
       * touch the server.
       *
       * The old path asks /api/paddle-details, which asks Paddle — and Paddle
       * has never heard of a Play subscriber, so it answers 'no_subscription'
       * and a PAYING user would be shown the free-tier pitch. That is the bug
       * this branch exists to prevent.
       *
       * There is also nothing to fetch: Google holds the card and the receipts,
       * we are not allowed to show them, and everything we DO display —
       * status, period end — is already mirrored in `current_subscription`.
       */
      // Wrapped: a failure here must never take down the Paddle path that
      // follows. This lookup is an optimisation for Play users, not a
      // precondition for anyone else — letting it throw would turn a
      // transient Supabase blip into a broken subscription page for
      // everybody.
      let local: CanonicalRow | null = null;
      try {
        const { data } = await supabase
          .from('current_subscription')
          .select(
            'provider,status,trial_ends_at,current_period_starts_at,current_period_ends_at,cancelled_at,provider_price_id',
          )
          .eq('user_id', user!.id)
          .maybeSingle();
        local = (data as CanonicalRow | null) ?? null;
      } catch {
        local = null;
      }

      // Play: Google's, not ours. Gift: nobody bills it. Neither has anything
      // for Paddle to report, so the local row is the whole story.
      if (local?.provider === 'google_play' || local?.provider === 'gift') {
        return {
          row: local,
          // Google's, not ours, and deliberately not shown.
          payment_method: null,
          transactions: null,
        };
      }

      try {
        const body = await authedPost<{
          row: CanonicalRow;
          payment_method: PaymentMethodSummary | null;
          transactions: TransactionSummary[] | null;
        }>('/api/paddle-details');
        return {
          row: body.row,
          payment_method: body.payment_method,
          transactions: body.transactions,
        };
      } catch (e) {
        if (e instanceof ManageError && e.code === 'no_subscription') {
          return { row: null, payment_method: null, transactions: null };
        }
        throw e;
      }
    },
  });
}

/** Merge a canonical row into both subscription caches so every consumer
 *  (paywall gates, profile summary, this screen) sees the verified state. */
function applyCanonicalRow(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  row: CanonicalRow,
): void {
  queryClient.setQueryData<CurrentSubscriptionRow | null>(
    SUBSCRIPTION_KEY(userId),
    (prev) => (prev ? { ...prev, ...row } : prev),
  );
  queryClient.setQueryData<SubscriptionDetails>(
    SUBSCRIPTION_DETAILS_KEY(userId),
    (prev) => (prev ? { ...prev, row: { ...(prev.row ?? row), ...row } } : prev),
  );
  // Background refetches keep everything honest even if a cache was cold.
  void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY(userId) });
  void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_DETAILS_KEY(userId) });
}

function useVerifiedMutation(path: '/api/paddle-cancel' | '/api/paddle-resume') {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const body = await authedPost<{ ok?: boolean; row: CanonicalRow }>(path);
      // Belt + braces on the trust contract: a 200 without an explicit
      // ok:true is NEVER success, whatever the server meant by it.
      if (body.ok !== true) {
        throw new ManageError(
          path === '/api/paddle-cancel' ? 'cancel_not_confirmed' : 'resume_not_confirmed',
          200,
        );
      }
      return body as { ok: true; row: CanonicalRow };
    },
    onSuccess: (body) => {
      if (user) applyCanonicalRow(queryClient, user.id, body.row);
    },
  });
}

export function useCancelSubscription() {
  return useVerifiedMutation('/api/paddle-cancel');
}

export function useResumeSubscription() {
  return useVerifiedMutation('/api/paddle-resume');
}

export interface ScheduledRedemption {
  coins: number;
  usd_cents: number;
  period_ends_at: string;
}

export const REDEMPTION_KEY = (userId: string) => ['subscription', 'redemption', userId] as const;

/** The pending coin discount for the next charge, if the redemption job
 *  scheduled one (RLS-scoped read; written only by the cron engine). */
export function useScheduledRedemption(): UseQueryResult<ScheduledRedemption | null> {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? REDEMPTION_KEY(user.id) : ['subscription', 'redemption', 'anon'],
    enabled: !!user,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ScheduledRedemption | null> => {
      const { data, error } = await supabase
        .from('coin_redemptions')
        .select('coins, usd_cents, period_ends_at')
        .eq('status', 'scheduled')
        // a line for an already-past renewal is stale — hide it
        .gte('period_ends_at', new Date().toISOString())
        .order('period_ends_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) return null; // cosmetic line — never break the money screen
      return data;
    },
  });
}

/** Mint the Paddle transaction for swapping the card on file (also collects
 *  the outstanding balance when past_due). Open it with openInlineCheckout. */
export async function mintCardUpdateTransaction(): Promise<string> {
  const body = await authedPost<{ ok: boolean; transaction_id: string }>('/api/paddle-card-txn');
  return body.transaction_id;
}
